/**
 * CCTV Module - ULTIMATE FIX (NO CLONES)
 * Solution: Disable Owl Carousel loop when only 1 item
 * This completely prevents clone creation
 *
 * @requires jQuery
 * @requires Owl Carousel
 * @requires Viewer.js
 * @requires DashboardAPI
 */

const CCTVModule = (() => {
  // Private variables
  let _carousel = null;
  let _viewer = null;
  let _currentStationId = null;
  let _cctvCount = 0;

  // Private methods

  /**
   * Initialize Owl Carousel
   * Dynamically disable loop if only 1 item to prevent cloning
   */
  const _initCarousel = () => {
    if (_carousel) {
      _carousel.trigger("destroy.owl.carousel");
      _carousel = null;
    }

    // CRITICAL FIX: Disable loop when there's only 1 item
    // This prevents Owl Carousel from creating clones
    const shouldLoop = _cctvCount > 1;

    _carousel = $("#carousel-cctv").owlCarousel({
      items: 1,
      autoplay: false,
      loop: shouldLoop, // ← KEY FIX: Only loop if more than 1 item
      nav: true,
      dots: true,
      navText: [
        '<i class="mdi mdi-chevron-left"></i>',
        '<i class="mdi mdi-chevron-right"></i>',
      ],
    });

    console.log(
      `🦉 Owl Carousel initialized (loop: ${shouldLoop}, items: ${_cctvCount})`,
    );
  };

  let _viewerLoad = null;

  const _viewerAsset = (kind) => {
    const section = document.getElementById("section-cctv");
    if (kind === "css") {
      return section?.dataset.viewerCss || "/lib/viewerjs/viewer.min.css";
    }
    return section?.dataset.viewerSrc || "/lib/viewerjs/viewer.min.js";
  };

  const _resolveViewer = () => {
    if (typeof window.Viewer === "function") return window.Viewer;
    if (typeof module !== "undefined" && typeof module.exports === "function") {
      window.Viewer = module.exports;
      return window.Viewer;
    }
    return null;
  };

  const _ensureViewerCss = () => {
    const href = _viewerAsset("css");
    if (document.querySelector(`link[data-cctv-viewer-css="1"], link[href="${href}"]`)) {
      return;
    }
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    link.dataset.cctvViewerCss = "1";
    document.head.appendChild(link);
  };

  const _ensureViewer = () => {
    const existing = _resolveViewer();
    if (existing) return Promise.resolve(existing);
    if (_viewerLoad) return _viewerLoad;

    _ensureViewerCss();
    _viewerLoad = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = _viewerAsset("js");
      script.async = false;
      script.onload = () => {
        const ctor = _resolveViewer();
        if (ctor) resolve(ctor);
        else reject(new Error("Viewer.js loaded but window.Viewer is missing"));
      };
      script.onerror = () => {
        _viewerLoad = null;
        reject(new Error(`Failed to load Viewer.js from ${script.src}`));
      };
      document.head.appendChild(script);
    });

    return _viewerLoad;
  };

  const _destroyViewer = () => {
    if (_viewer) {
      _viewer.destroy();
      _viewer = null;
    }
    $("#cctv-viewer-source").remove();
  };

  const _localizeViewerToolbar = () => {
    const labels = {
      "zoom-in": "Perbesar",
      "zoom-out": "Perkecil",
      "one-to-one": "Ukuran asli",
      reset: "Reset",
      prev: "Sebelumnya",
      next: "Berikutnya",
    };

    document
      .querySelectorAll(".viewer-toolbar [data-viewer-action]")
      .forEach((button) => {
        const action = button.getAttribute("data-viewer-action");
        if (labels[action]) button.setAttribute("title", labels[action]);
      });
  };

  /**
   * Viewer.js lightbox, including built-in zoom controls.
   * Images live outside the carousel so Owl clones are not duplicated.
   */
  const _initViewer = (cctvList, nowMillis) => {
    _destroyViewer();

    const ViewerCtor = _resolveViewer();
    if (!ViewerCtor) {
      _ensureViewer()
        .then(() => _initViewer(cctvList, nowMillis))
        .catch((error) => console.error(error));
      return;
    }

    _ensureViewerCss();

    const source = document.createElement("div");
    source.id = "cctv-viewer-source";
    source.hidden = true;

    cctvList.forEach((cctv) => {
      const img = document.createElement("img");
      img.src = `${cctv.url}?v=${nowMillis}`;
      img.alt = cctv.name || "CCTV";
      source.appendChild(img);
    });
    document.body.appendChild(source);

    const hasGallery = _cctvCount > 1;
    _viewer = new ViewerCtor(source, {
      navbar: hasGallery,
      title: true,
      tooltip: true,
      movable: true,
      zoomable: true,
      zoomOnWheel: true,
      zoomOnTouch: true,
      slideOnTouch: false,
      toggleOnDblclick: true,
      loop: hasGallery,
      toolbar: {
        zoomIn: 4,
        zoomOut: 4,
        oneToOne: 4,
        reset: 4,
        prev: hasGallery ? 4 : 0,
        play: 0,
        next: hasGallery ? 4 : 0,
        rotateLeft: 0,
        rotateRight: 0,
        flipHorizontal: 0,
        flipVertical: 0,
      },
      shown: _localizeViewerToolbar,
      viewed: _localizeViewerToolbar,
    });
  };

  const _openViewer = (index) => {
    if (!Number.isFinite(index)) return;
    if (_viewer) {
      _viewer.view(index);
      return;
    }
    _ensureViewer()
      .then(() => {
        if (_viewer) _viewer.view(index);
      })
      .catch((error) => console.error(error));
  };

  const _renderEmptyState = (title, description, variant = "default") => {
    _destroyViewer();
    $("#section-cctv").html(
      DashboardHelpers.renderSidebarEmpty({
        icon: variant === "error" ? "mdi-alert-circle-outline" : "mdi-cctv-off",
        title,
        description,
        variant,
        compact: true,
      }),
    );
  };

  /**
   * Show loading state
   */
  const _showLoading = () => {
    _destroyViewer();
    $("#section-cctv").html(DashboardHelpers.renderCctvLoadingSkeleton());
  };

  /**
   * Show no CCTV message
   */
  const _showNoCCTV = () => {
    _renderEmptyState(
      "CCTV Tidak Tersedia",
      "Pos ini belum memiliki kamera CCTV yang terdaftar atau feed belum aktif.",
    );
  };

  /**
   * Render CCTV items
   */
  const _renderCCTVItems = (cctvList) => {
    // Store count for carousel initialization
    _cctvCount = cctvList.length;

    let carouselHtml =
      '<div class="owl-carousel owl-theme" id="carousel-cctv">';
    const nowMillis = Date.now();

    cctvList.forEach((cctv, index) => {
      // Add timestamp to prevent caching
      const imageUrl = `${cctv.url}?v=${nowMillis}`;

      carouselHtml += `
      <div class="item">
        <div class="card border-0 shadow-sm">
          
          <!-- Gambar CCTV - Lebih kecil -->
          <div class="position-relative bg-secondary" style="height: 280px!important;">
            <a href="${imageUrl}" class="cctv-popup" data-index="${index}" title="${cctv.name}">
                <img src="${imageUrl}" 
                    class="w-100 h-100" 
                    referrerpolicy="no-referrer"
                    alt="${cctv.name}">
            </a>
            
            <!-- Label Offline -->
            <div class="position-absolute top-50 start-50 translate-middle text-center">
             
            </div>
          </div>
          
          <!-- Footer -->
          <div class="card-footer bg-dark text-white py-1 px-2">
            <div class="d-flex justify-content-between align-items-center">
              <div>
               
                <span class="fw-bold" style="font-size: 0.8rem;">${cctv.name}</span>
              </div>
              <a href="${imageUrl}" class="cctv-popup text-white" data-index="${index}" title="${cctv.name}">
                <i class="mdi mdi-eye"></i>
              </a>
            </div>
          </div>
          
        </div>
      </div>
    `;
    });

    carouselHtml += "</div>";

    $("#section-cctv").empty();
    $("#section-cctv").append(carouselHtml);

    _initCarousel();
    _initViewer(cctvList, nowMillis);
  };

  // Public API
  return {
    /**
     * Initialize CCTV module
     */
    init() {
      $("#section-cctv").on("click", ".cctv-popup", function (event) {
        event.preventDefault();
        _openViewer(Number($(this).attr("data-index")));
      });

      _renderEmptyState(
        "Pilih Pos Monitoring",
        "Klik salah satu pos di daftar untuk menampilkan feed CCTV.",
      );
    },

    /**
     * Load CCTV list for station
     */
    async load(stationId = "all") {
      try {
        _currentStationId = stationId;
        _showLoading();

        const response = await DashboardAPI.getCCTV(stationId);

        if (response && response.length > 0) {
          _renderCCTVItems(response);
        } else {
          _showNoCCTV();
        }
      } catch (error) {
        console.error("❌ Error loading CCTV:", error);
        _renderEmptyState(
          "Gagal Memuat CCTV",
          "Terjadi kesalahan saat memuat feed CCTV. Silakan coba lagi.",
          "error",
        );
      }
    },

    /**
     * Refresh current CCTV
     */
    refresh() {
      if (_currentStationId) {
        this.load(_currentStationId);
      }
    },

    /**
     * Destroy carousel
     */
    destroy() {
      if (_carousel) {
        _carousel.trigger("destroy.owl.carousel");
        _carousel = null;
      }

      _destroyViewer();
      _cctvCount = 0;
    },
  };
})();

// Expose to global scope
window.CCTVModule = CCTVModule;
